import React, { ForwardRefRenderFunction, forwardRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
//#region redux
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { ThunkDispatch } from "redux-thunk";
import { displayErrorMessage } from "store/actions/utilActions";
import { updateBusinessUnitManagerSettings } from "store/actions/authActions";
//#endregion
//#region types
import { SettingsFormTypes } from "models/formTypes";
import { EditableTableMoreActionMenuItemType, FormHandles } from "models/propTypes/genericFormProps";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { EmployeesSettingsFormContainerProps } from "models/propTypes/settingsPropTypes";
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { FormikProps } from "formik";
//#endregion
//#region components
import EmployeesSettingsForm from "./employeesSettingsForm";
import { Skeletons, useMountedState } from "components/common";
import { useEmployeeHelpers, useRemoveEmployeeDialog } from "components/employee";
import { isEmployeeSalaryActive } from "components/employee/employeeHelpers";
import { useEmployeeService, useSettingsService } from "serviceHooks";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance, UrlHelper, UtilsHelper } from "utils/helpers";

const EmployeesSettingsFormContainer: ForwardRefRenderFunction<FormHandles, EmployeesSettingsFormContainerProps> = (
  props,
  ref
) => {
  const settingsService = useSettingsService();
  const employeeService = useEmployeeService();

  const location = useLocation();
  const navigate = useNavigate();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loggedInUser] = useSelector((state: AppState) => [state.auth.loggedInUser]);
  const businessUnitManagerSettings = useSelector((state: AppState) => state.auth.businessUnitManagerSettings);
  const [defaultValues, setDefaultValues] = React.useState<SettingsFormTypes.EmployeesSettings>();
  const [userTypesEnabled, setUserTypesEnabled] = React.useState<boolean>(false);
  const [enabledUserTypes, setEnabledUserTypes] = React.useState<WintSuperkollAPIModel.UserTypeDto[]>([]);
  const { getMounted } = useMountedState();
  const { canView, canManageEmployeeSalary } = useEmployeeHelpers();
  const removeEmployeeDialog = useRemoveEmployeeDialog();
  const { isWintSalarySettingsAvailable } = useSalaryPermissions();

  const validationSchema = Yup.object<SettingsFormTypes.EmployeesSettings>({
    deviationsViaMobile: Yup.boolean(),
    defaultSalaryDeviationPersonId: Yup.number(),
    defaultSalaryDeviationPersonName: Yup.string(),
    isSalaryAdmin: Yup.boolean().required().oneOf([true]),
    superkollSalaryType: Yup.number()
      .required()
      .oneOf([WintSuperkollAPIModel.SuperkollSalaryType.Deviations, WintSuperkollAPIModel.SuperkollSalaryType.Simple]),
  });

  const superkollSalaryType = useSelector((state: AppState) => state.auth.selectedCompany.superkollSalaryType);
  const createSalaryAutoFromSalaryPersonEntry = useSelector(
    (state: AppState) => state.auth.selectedCompany.createSalaryAutoFromSalaryPersonEntry
  );

  const handleSubmit = (values: SettingsFormTypes.EmployeesSettings) => {
    const employeesPut = {
      canAddDeviationViaMobileApp: values.deviationsViaMobile,
      defaultSalaryDeviationDraftReceiverId: values.defaultSalaryDeviationPersonId,
    } as WintSuperkollAPIModel.EmployeeSettingsPut;
    return settingsService.updateEmployeesSettings(employeesPut).then(() => {
      setDefaultValues(values);
    });
  };

  const salaryActivateToggleHandle = async (
    rowIndex: number,
    row: WintSuperkollAPIModel.EmployeeItem,
    formikProps: FormikProps<SettingsFormTypes.EmployeesSettings>
  ) => {
    const { values, setFieldValue } = formikProps;

    const getUpdatedEmployees = () => {
      const updatedEmployees = values.employees ? [...values.employees] : [];
      updatedEmployees.forEach((x) => {
        if (x.id === row.id && x.salaryInfo) x.salaryInfo.isActive = !x.salaryInfo.isActive;
      });

      return updatedEmployees;
    };

    if (row.salaryInfo) {
      if (!row.salaryInfo.isActive) {
        let errorMessage: string | undefined;
        const companyUsesSimpleSalaryAuto =
          superkollSalaryType === WintSuperkollAPIModel.SuperkollSalaryType.Simple &&
          createSalaryAutoFromSalaryPersonEntry;

        if (!row.personnumber && companyUsesSimpleSalaryAuto) {
          errorMessage = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.PersonNumberRequired);
        } else if (
          UtilsHelper.isEmpty(row.salaryInfo.bankAccount) ||
          UtilsHelper.isEmpty(row.salaryInfo.bankAccountClearing)
        ) {
          errorMessage = `${LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Settings.BankAccountMissing
          )}, ${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.ClearingNumberMissing)}`;
        }

        errorMessage
          ? dispatch(displayErrorMessage(errorMessage))
          : employeeService.activateSalary(row.id).then((res) => {
              setFieldValue(FormFieldConstants.Settings.EmployeesSettings.Employees, getUpdatedEmployees());
            });
      } else {
        employeeService.deactivateSalary(row.id).then((res) => {
          setFieldValue(FormFieldConstants.Settings.EmployeesSettings.Employees, getUpdatedEmployees());
        });
      }
    }
  };

  const addOrEditEmployee = (values?: WintSuperkollAPIModel.EmployeeItem) => {
    navigate(UrlHelper.formatPath(`/Employee/${values && values.id ? values.id : "Create"}`), {
      state: { background: location },
    });
  };

  const capitalize = (word: string) => {
    if (typeof word !== "string") return "";
    return word.charAt(0).toUpperCase() + word.slice(1);
  };

  const getPermissions = (value: WintSuperkollAPIModel.EmployeeItem) => {
    const validPermissions =
      value.permissions &&
      Object.entries(value.permissions)
        .filter(([item, val]) => val)
        .map(([item, val]) => LocalizationHelperInstance.getFormattedMessage(`Permission.${capitalize(item)}`));
    return validPermissions ? validPermissions.join(", ") : "";
  };

  const onRemoveEmployeeSubmitSuccess = (
    row: WintSuperkollAPIModel.EmployeeItem,
    formikProps: FormikProps<SettingsFormTypes.EmployeesSettings>
  ): any => {
    const { values, setFieldValue } = formikProps!;
    const updatedEmployees = values.employees?.filter((emp) => emp.id !== row.id);

    if (getMounted()) {
      setFieldValue(FormFieldConstants.Settings.EmployeesSettings.Employees, updatedEmployees);
    }

    if (props.isBusinessUnitManagerWithSalaryRights) {
      const updatedManageableEmployees = businessUnitManagerSettings.manageableEmployees.filter((id) => id !== row.id);
      dispatch(
        updateBusinessUnitManagerSettings({
          ...businessUnitManagerSettings,
          manageableEmployees: updatedManageableEmployees,
        })
      );
    }
  };

  const getEmployeesMoreActions = (
    row: WintSuperkollAPIModel.EmployeeItem,
    formikProps: FormikProps<SettingsFormTypes.EmployeesSettings>
  ) => {
    const actions: EditableTableMoreActionMenuItemType[] = [];
    !isWintSalarySettingsAvailable &&
      canManageEmployeeSalary(row.id) &&
      actions.push({
        description: LocalizationHelperInstance.getFormattedMessage(
          row.salaryInfo?.isActive ? TranslationKeys.Settings.DeactivateSalary : TranslationKeys.Settings.ActivateSalary
        ),
        icon: IconDefinitionConstants.WStop,
        highlightType: row.salaryInfo?.isActive ? StyleEnums.DisplayType.Danger : undefined,
        onClickHandler: salaryActivateToggleHandle,
      });

    loggedInUser.personId !== row.id &&
      canView(row.id) &&
      !(isWintSalarySettingsAvailable && isEmployeeSalaryActive(row, isWintSalarySettingsAvailable)) &&
      actions.push({
        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.DeleteUser),
        icon: IconDefinitionConstants.WTrashAlt,
        highlightType: StyleEnums.DisplayType.Danger,
        actionDialog: removeEmployeeDialog(row, onRemoveEmployeeSubmitSuccess.bind(null, row, formikProps)),
      });

    actions.push({
      description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.EditUser),
      icon: IconDefinitionConstants.WEdit,
      onClickHandler: async (index: number, row: WintSuperkollAPIModel.EmployeeItem, formikProps: any) =>
        addOrEditEmployee(row),
    });
    return actions;
  };

  React.useEffect(() => {
    settingsService.getEmployeesSettings().then((employeesSettings) => {
      if (getMounted()) {
        setDefaultValues({
          deviationsViaMobile: employeesSettings?.canAddDeviationViaMobileApp,
          defaultSalaryDeviationPersonName: employeesSettings?.defaultSalaryDeviationDraftReceiverName,
          defaultSalaryDeviationPersonId: employeesSettings?.defaultSalaryDeviationDraftReceiverId,
          employees: employeesSettings?.employees?.map((emp) => {
            return emp.salaryInfo?.monthlySalary
              ? emp
              : ({ ...emp, salaryInfo: { ...emp.salaryInfo, monthlySalary: 0 } } as WintSuperkollAPIModel.EmployeeItem);
          }),
          isSalaryAdmin: props.isSalaryAdmin,
          superkollSalaryType: props.superkollSalaryType,
        });
        setLoading(false);
      }
    });

    settingsService
      .getCompanySettings()
      .then((companySettings) => {
        if (getMounted()) {
          setUserTypesEnabled(companySettings.userTypesEnabled ?? false);
          setEnabledUserTypes(companySettings.enabledUserTypes ?? []);
        }
      })
      .catch(() => {
        if (getMounted()) {
          setUserTypesEnabled(false);
          setEnabledUserTypes([]);
        }
      });
  }, []);

  return loading ? (
    <Skeletons.FormSkeleton />
  ) : (
    <EmployeesSettingsForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      defaultValues={defaultValues}
      getEmployeePermissions={getPermissions}
      addOrEditEmployee={addOrEditEmployee}
      employeesMoreActions={getEmployeesMoreActions}
      userTypesEnabled={userTypesEnabled}
      enabledUserTypes={enabledUserTypes}
    />
  );
};

export default forwardRef(EmployeesSettingsFormContainer);
